import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Award, MapPin, Star, ArrowRight, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { fetchPractitioners } from '../api';
import { Badge } from '../components/ui/badge';

const WHATSAPP_NUMBER = '918279765078';

const Practitioners = () => {
  const [selectedSpecialization, setSelectedSpecialization] = useState('all');
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPractitioners()
      .then((data) => setPractitioners(data))
      .catch(() => setPractitioners([]))
      .finally(() => setLoading(false));
  }, []);

  const bookOnWhatsApp = (practitioner) => {
    const message = encodeURIComponent(
      `Hello Aatreya Herbals! I want to book an appointment with ${practitioner.name} (${practitioner.specialization}). Consultation Fee: ₹${practitioner.consultationFee}. Please share available slots.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const specializations = ['all', ...new Set(practitioners.map(p => p.specialization))];

  const filteredPractitioners = selectedSpecialization === 'all'
    ? practitioners
    : practitioners.filter(p => p.specialization === selectedSpecialization);

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero */}
      <section className="bg-primary/5 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4"
          >
            <h1 className="text-5xl sm:text-6xl font-bold font-serif">
              Our Expert Practitioners
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Certified Ayurvedic doctors with decades of combined experience in holistic healing
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        {/* Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap gap-3 justify-center mb-12"
        >
          {specializations.map((spec) => (
            <Button
              key={spec}
              variant={selectedSpecialization === spec ? 'default' : 'outline'}
              onClick={() => setSelectedSpecialization(spec)}
              className="capitalize"
            >
              {spec === 'all' ? 'All Specialists' : spec}
            </Button>
          ))}
        </motion.div>

        {/* Practitioners Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filteredPractitioners.map((practitioner, index) => (
            <motion.div
              key={practitioner.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-card rounded-2xl border border-border shadow-lg hover:shadow-2xl transition-all overflow-hidden"
            >
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/3 relative">
                  <img
                    src={practitioner.image}
                    alt={`${practitioner.name} - ${practitioner.specialization} at Aatreya Herbals`}
                    className="w-full h-64 md:h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                    <Star className="w-4 h-4 fill-current" />
                    <span>{practitioner.rating}</span>
                  </div>
                </div>

                <div className="md:w-2/3 p-6 space-y-4">
                  <div>
                    <h3 className="text-2xl font-serif font-bold">{practitioner.name}</h3>
                    <p className="text-primary font-medium">{practitioner.qualification}</p>
                  </div>

                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <Award className="w-5 h-5" />
                    <span className="text-sm">{practitioner.specialization}</span>
                  </div>

                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <Clock className="w-5 h-5" />
                    <span className="text-sm">{practitioner.experience} years experience</span>
                  </div>

                  <p className="text-sm text-muted-foreground">{practitioner.about}</p>

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Languages:</p>
                    <div className="flex flex-wrap gap-2">
                      {practitioner.languages.map((lang) => (
                        <Badge key={lang} variant="secondary">{lang}</Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Available:</p>
                    <div className="flex flex-wrap gap-2">
                      {practitioner.availability.map((day) => (
                        <Badge key={day} variant="outline">{day}</Badge>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-primary">₹{practitioner.consultationFee}</span>
                        <span className="text-sm text-muted-foreground ml-2">per session</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/book-appointment?practitioner=${practitioner.id}`} className="flex-1">
                        <Button className="w-full bg-primary hover:bg-primary/90 group">
                          Book Now
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                      <Button
                        onClick={() => bookOnWhatsApp(practitioner)}
                        className="bg-green-500 hover:bg-green-600 text-white"
                        data-testid={`whatsapp-book-${practitioner.id}`}
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        WhatsApp
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Practitioners;